<template>
  <div>
    <div class="form-input clickable" :class="[ size, { disabled: disabled, active: value, focused: draggingOver, 'border-danger': errors && errors.length > 0 } ]" @click="fileInput.click()" @mouseover="hovering = true" @mouseout="hovering = false" @dragover.prevent="draggingOver = true" @dragleave="draggingOver = false" @drop.prevent="drop">
      <label v-if="title && title.length" :for="randomId" class="form-input-label">
        <span>{{ title }}</span> <span v-if="required" class="ms-2"><i class="fas fa-asterisk text-danger" style="font-size: 0.7em"></i></span>
      </label>
      <div class="form-input-body" :id="randomId">
        <div class="d-flex flex-column">
          <div class="d-flex align-items-center justify-content-center flex-wrap g-2" v-if="value && !multiple || multiple && value && value.length > 0 || uploading.length > 0">
            <div class="d-inline-block" v-for="(file, index) in (multiple ? value : value ? [value] : [])" :key="index">
              <div class="position-relative file-input-preview" style="height: 5.5em; width: 5.5em; cursor: initial">
                <div class="d-flex align-items-center justify-content-center w-100 h-100">
                  <FileInputPreviewView :preview="previews[file.identifier]"/>
                </div>
                <div class="file-input-preview-options position-absolute bottom-0 top-0 start-0 end-0 p-2 rounded-3">
                  <div class="d-flex flex-column justify-content-between g-2 h-100 w-100">
                    <div class="text-center my-1" style="font-size: 0.8em; white-space: nowrap; text-overflow: ellipsis; overflow: hidden;">
                      {{ file.name }}
                    </div>
                    <ButtonView type="normal" color="danger" size="small" @click.stop="removeFile(index)">
                      <i class="fas fa-trash"></i>
                    </ButtonView>
                  </div>
                </div>
              </div>
            </div>
            <div class="d-inline-block" v-for="(request, index) in uploading" :key="index">
              <div class="position-relative file-input-preview" style="height: 5.5em; width: 5.5em; cursor: initial">
                <div class="d-flex align-items-center justify-content-center w-100 h-100" style="opacity: 0.4">
                  <FileInputPreviewView :preview="{ url: getUrlForFile(request.file), type: request.file.type }"/>
                </div>
                <div class="position-absolute top-0 bottom-0 start-0 end-0 p-2 d-flex align-items-center justify-content-center">
                  <GaugeView :value="request.progress" :max="100" color="primary" style="height: 70%; width: 70%"/>
                </div>
                <div class="file-input-preview-options position-absolute bottom-0 top-0 start-0 end-0 p-2 rounded-3">
                  <div class="d-flex flex-column justify-content-between g-2 h-100 w-100">
                    <div class="bg-secondary rounded-2" style="font-size: 0.8em; padding: 0.1em 0.3em; white-space: nowrap; text-overflow: ellipsis; overflow: hidden;">
                      {{ getFileDisplayName(request.file) }}
                    </div>
                    <ButtonView type="normal" color="danger" size="small" @click.stop="request.controller.abort()">
                      <i class="fas fa-xmark-circle"></i>
                    </ButtonView>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="d-flex align-items-center justify-content-center">
            <input ref="fileInput" type="file" :multiple="multiple" class="d-none position-absolute" @change="inputChanged" :accept="stringifiedExtensions"/>
            <i v-if="(!value || value.length === 0) && uploading.length === 0 || multiple" class="fa-duotone fa-cloud-arrow-up text-foreground m-4" style="font-size: 3em"></i>
          </div>
        </div>
      </div>
    </div>
    <div v-if="errors && errors.length > 0" class="form-input-errors">
      <div v-for="(error, index) in errors" :key="index" class="text-danger">{{ error }}</div>
    </div>
  </div>
</template>

<script setup>
import {computed, reactive, ref, watch} from "vue";
import ButtonView from "../../general/ButtonView.vue";
import axios from "axios";
import toasts from "../../../plugins/toasts/toasts.js";
import GaugeView from "../../general/GaugeView.vue";
import FileInputPreviewView from "./FileInputPreviewView.vue";

const props = defineProps({
  title: String,
  value: [Object, Array],
  required: Boolean,
  disabled: Boolean,
  errors: Array,
  multiple: Boolean,
  extensions: Array,
  endpoint: String,
  method: String,
  size: {
    type: String,
    default: "medium",
    validator(value) {
      return ["small", "medium", "large"].includes(value);
    }
  },
});

const emit = defineEmits(["update:value"]);

const fileInput = ref(undefined);

const randomId = "input-" + Math.random().toString();

const uploading = ref([]);

const draggingOver = ref(false);
const hovering = ref(false);

const previews = ref({});

const stringifiedExtensions = computed(() => {
  return props.extensions?.map(item => "." + item).join(",") ?? "";
});

function inputChanged(event) {
  upload(event.target.files[0]);
  event.target.value = "";
}

watch(() => props.value, () => {
  if (props.value) {
    if (props.multiple) {
      props.value.forEach(file => downloadPreview(file));
    } else {
      downloadPreview(props.value);
    }
  }
}, {
  immediate: true
});

function downloadPreview(file) {
  if (previews.value[file.identifier] !== undefined) return;
  const newAxios = axios.create({
    baseURL: undefined,
    withCredentials: false
  });
  newAxios.get(file.url, {
    responseType: "blob",
    headers: {
      'Cache-Control': 'no-cache',
    }
  }).then(response => {
    previews.value[file.identifier] = {
      url: URL.createObjectURL(response.data),
      type: response.data.type
    };
  }).catch(error => {
    previews.value[file.identifier] = null;
    console.error(error);
  });
}

function getUrlForFile(file) {
  return URL.createObjectURL(file);
}

function removeFile(index) {
  if (props.multiple) {
    let array = JSON.parse(JSON.stringify(props.value ?? []));
    array.splice(index, 1);
    emit("update:value", array);
  } else {
    emit("update:value", undefined);
  }
}

function drop(event) {
  draggingOver.value = false;
  if (props.multiple) {
    [...event.dataTransfer.files].forEach(file => {
      upload(file);
    });
  } else {
    upload(event.dataTransfer.files[0]);
  }
  fileInput.value.value = "";
}

function getFileDisplayName(file) {
  if (file.name.length > 18) {
    let parts = file.name.split(".");
    if (parts.length > 1) {
      return parts[0].substring(0, 12) + "..." + file.name.substring(file.name.length - Math.max(parts[parts.length - 1].length, 5), file.name.length);
    } else {
      return file.name.substring(0, 18);
    }
  } else {
    return file.name;
  }
}

function upload(file) {
  const uploadId = Math.random().toString();
  const controller = new AbortController();
  const upload = reactive({
    file: file,
    progress: 0,
    id: uploadId,
    controller: controller
  });
  uploading.value = [...uploading.value, upload];
  const formData = new FormData();
  formData.append("file", file);
  axios.request({
    url: props.endpoint,
    method: props.method,
    headers: {
      "Content-Type": "multipart/form-data"
    },
    data: formData,
    signal: controller.signal,
    onUploadProgress: progressEvent => {
      upload.progress = progressEvent.progress * 100;
    }
  }).then(response => {
    const uploadedFile = {
      url: response.data.url,
      name: response.data.name,
      identifier: response.data.identifier,
      extension: response.data.extension
    };
    if (props.multiple) {
      emit("update:value", [...(props.value ?? []), uploadedFile]);
    } else {
      emit("update:value", uploadedFile);
    }
  }).catch(error => {
    if (!axios.isCancel(error)) {
      toasts.create({
        color: "danger",
        content: error.message
      });
    }
  }).finally(() => {
    uploading.value = uploading.value.filter(item => item.id !== uploadId);
  });
}

</script>

<style scoped>
  :deep(.file-input-preview-options) {
    display: none !important;
  }
  :deep(.file-input-preview) {
    border: calc(1em * var(--border-width-factor)) solid rgb(var(--color-border), 0.75);
    border-radius: calc(1em * var(--border-radius-factor));
    overflow: hidden;
  }
  :deep(.file-input-preview:hover .file-input-preview-options) {
    display: block !important;
    background-color: rgba(var(--color-border), 0.4) !important;
    border-radius: calc(1em * var(--border-radius-factor));
  }
</style>
